// Bridges the context-free transport layer (req()) to a single root-mounted React modal host.
// req() calls requestStepUpToken(scope); the host subscribes, renders the OTP modal, and resolves
// with the fresh scoped token (or rejects on cancel). Single-flight per scope: if several requests
// 403 at once, one OTP satisfies them all — mirrors tryRefresh()'s single-flight.
export type StepUpRequest = { scope: string; resolve: (token: string) => void; reject: (err: Error) => void };

let active: StepUpRequest | null = null;
const inflight = new Map<string, Promise<string>>();
const subs = new Set<(req: StepUpRequest | null) => void>();

// The elevated token is short-lived but NOT single-use: the IdP mints it with a real TTL
// (scopes.step_up_ttl, default 120s). Cache it per scope and reuse it until it expires, so a burst of
// sensitive actions costs ONE OTP instead of one SMS per action. Cleared on logout via clearStepUp().
type CachedToken = { token: string; expiresAt: number };
const cache = new Map<string, CachedToken>();

// Retire a token slightly BEFORE its true expiry so it can't die in flight (network + server clock
// skew). Mirrors the session token's proactive refresh.
const SKEW_MS = 5_000;

function liveToken(scope: string): string | null {
  const hit = cache.get(scope);
  if (!hit) return null;
  if (Date.now() >= hit.expiresAt) { cache.delete(scope); return null; } // expired → force a new OTP
  return hit.token;
}

/** Seed the cache after a successful OTP exchange. `expiresIn` is the grant's seconds (0 ⇒ don't cache). */
export function cacheStepUpToken(scope: string, token: string, expiresIn: number): void {
  if (!(expiresIn > 0)) return; // unknown/absent TTL ⇒ treat as single-use, never reuse blindly
  cache.set(scope, { token, expiresAt: Date.now() + expiresIn * 1000 - SKEW_MS });
}

/** Peek at a still-valid elevated token WITHOUT prompting (null if none). Lets the transport pre-attach
 *  it and skip a 403 it already knows is coming. Never prompts — a miss just means "send unelevated and
 *  let the server challenge us", i.e. the normal path. */
export function peekStepUpToken(scope: string): string | null { return liveToken(scope); }

// What the SERVER told us a given endpoint requires, learned from its 403 challenge. We never GUESS a
// route's scope (that coupling is exactly what the server-authoritative design removes) — we only
// remember what we were explicitly told, so the SECOND sensitive call to the same endpoint can attach a
// live elevated token up front instead of eating another 403. If the mapping ever goes stale (the route's
// scope changed), the pre-attached token simply fails the guard and we fall back to the 403 path: safe.
const routeScope = new Map<string, string>();

/** Remember the scope the server demanded for `key` (method + path). */
export function rememberRouteScope(key: string, scope: string): void { routeScope.set(key, scope); }

/** The scope the server previously demanded for `key`, if we have been challenged on it before. */
export function knownRouteScope(key: string): string | undefined { return routeScope.get(key); }

/** Drop every elevated token AND abort any prompt still open (call on logout / session change — an
 *  elevated token must not outlive the session that earned it, and a modal left waiting on a dead
 *  session would hang its caller forever). */
export function clearStepUp(): void {
  cache.clear();
  active?.reject(new Error("step_up_cancelled")); // also clears `active` + `inflight` via its clear()
}

export function subscribeStepUp(cb: (req: StepUpRequest | null) => void): () => void {
  subs.add(cb);
  cb(active); // sync current state to a late subscriber
  return () => { subs.delete(cb); };
}

export function requestStepUpToken(scope: string): Promise<string> {
  const cached = liveToken(scope);
  if (cached) return Promise.resolve(cached); // still inside the TTL → replay with it, no OTP
  const existing = inflight.get(scope);
  if (existing) return existing;
  const p = new Promise<string>((resolve, reject) => {
    const clear = () => { active = null; inflight.delete(scope); emit(); };
    active = {
      scope,
      resolve: (t) => { clear(); resolve(t); },
      reject: (e) => { clear(); reject(e); },
    };
    emit();
  });
  inflight.set(scope, p);
  return p;
}

function emit() { for (const cb of subs) cb(active); }
